import { forwardRef } from "react";
import { IoMdTrendingDown, IoMdTrendingUp } from "react-icons/io";
import { cn } from "@/lib/utils";
import {
  getHeadingAndSubheadingForTotalImpressions,
  getHeadingAndSubheadingForTotalWords,
  getStreakHeading,
} from "../YearWrap2024Helpers";
import { FaAward, FaHeart } from "react-icons/fa";
import { useAuth } from "@/Providers/Auth";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

interface WrappedProps {
  width?: number;
  height?: number;
  className?: string;
  cardValue?: string | number;
  heading?: string;
  subheading?: string;
  footerText?: string;
  containerClassName?: string;
}
interface MonthlyTrendData {
  _id: number;
  avgEngagement: number;
  totalEngagement: number;
  totalPost: number;
}

export const Wrapped2024TotalWords = forwardRef<HTMLDivElement, WrappedProps>(
  (
    { width = 200, height = 200, containerClassName, className, cardValue },
    ref
  ) => {
    const scale = width / 200;
    const { heading, subheading } = getHeadingAndSubheadingForTotalWords(
      parseInt(cardValue?.toString())
    );

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-total-words"
        children={
          <div
            // ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Total+Words.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col z-10">
              <div
                className="flex flex-col items-center"
                style={{ gap: `${scale * 6}px` }}
              >
                <span
                  className="text-white text-center not-italic font-normal"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    lineHeight: "normal",
                  }}
                >
                  {heading ?? "You wrote a lot this year"}
                </span>
                <span
                  className="text-white not-italic font-bold"
                  style={{
                    fontSize: `${scale * 17.778}px`,
                    lineHeight: "normal",
                  }}
                >
                  {cardValue?.toLocaleString() ?? 50000} characters
                </span>
              </div>
              <span
                className="text-white text-center italic font-light"
                style={{
                  marginTop: `${scale * 22.5}px`,
                  fontSize: `${scale * 6.7}px`,
                  lineHeight: "normal",
                }}
              >
                {subheading ?? (
                  <span>
                    That's almost{" "}
                    <span
                      className="text-white italic font-bold"
                      style={{
                        fontSize: `${scale * 6.77}px`,
                        lineHeight: "normal",
                      }}
                    >
                      a mini thriller
                    </span>{" "}
                    novel!
                  </span>
                )}
              </span>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024FavorableMonth = forwardRef<
  HTMLDivElement,
  WrappedProps
>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      cardValue,
      heading,
    },
    ref
  ) => {
    const scale = width / 200;

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-favorable-month"
        children={
          <div
            ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Favourable+Month.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10">
              <div
                className="flex flex-col items-center"
                style={{ gap: `${scale * 6}px` }}
              >
                <span
                  className="text-black text-center not-italic font-medium"
                  style={{
                    fontSize: `${scale * 8.8}px`,
                    lineHeight: "normal",
                  }}
                >
                  {heading ?? "People liked you the most in"}
                </span>
                <span
                  className="text-black text-center not-italic font-bold"
                  style={{
                    fontSize: `${scale * 20}px`,
                    lineHeight: "normal",
                  }}
                >
                  {cardValue ?? "September"} 💙
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024TotalImpressions = forwardRef<
  HTMLDivElement,
  WrappedProps
>(
  (
    { width = 200, height = 200, containerClassName, className, cardValue },
    ref
  ) => {
    const scale = width / 200;
    const { heading, subheading } = getHeadingAndSubheadingForTotalImpressions(
      parseInt(cardValue?.toString())
    );

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-total-impressions"
        children={
          <div
            ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Total+Impressions.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10 ">
              <div
                className="flex flex-col items-center"
                style={{
                  width: `${scale * 139.259}px`,
                  gap: `${scale * 19.259}px`,
                }}
              >
                <div
                  className="flex flex-col items-center"
                  style={{ gap: `${scale * 4.444}px` }}
                >
                  <span
                    className="text-white not-italic font-normal"
                    style={{
                      fontSize: `${scale * 7.407}px`,
                      lineHeight: "normal",
                    }}
                  >
                    {heading ?? "Your thoughts impacted"}
                  </span>
                  <span
                    className="text-white text-center not-italic font-bold"
                    style={{
                      fontSize: `${scale * 17.778}px`,
                      lineHeight: "normal",
                    }}
                  >
                    {cardValue?.toLocaleString() || 1000000}
                  </span>
                </div>
                <span
                  className="text-white text-center italic font-light"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    lineHeight: `${scale * 10.37}px`,
                  }}
                >
                  {subheading ?? "That's more than the population of Hong Kong"}
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024FavorableDayOfWeek = forwardRef<
  HTMLDivElement,
  WrappedProps
>(
  (
    { width = 200, height = 200, containerClassName, className, cardValue },
    ref
  ) => {
    const scale = width / 200;

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-favorable-day"
        children={
          <div
            ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Favourable+Week.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10">
              <div className="flex flex-col items-center gap-2">
                <span
                  className="text-black text-center not-italic font-medium"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    lineHeight: "normal",
                  }}
                >
                  A day that turned out to be your{" "}
                  <span className="italic">luckiest</span> day{" "}
                </span>
                <span
                  className="text-[#3276F8] text-center not-italic font-semibold"
                  style={{
                    fontSize: `${scale * 20.741}px`,
                    lineHeight: "normal",
                  }}
                >
                  {cardValue || "Wednesday"}🤞🏼
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024LongestStreak = forwardRef<
  HTMLDivElement,
  WrappedProps & {
    startDate: string;
    endDate: string;
  }
>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      cardValue,
      startDate,
      endDate,
    },
    ref
  ) => {
    const scale = width / 200;
    const heading = getStreakHeading(parseInt(cardValue?.toString()));

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-longest-streak"
        children={
          <div
            ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Longest+Streak.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10">
              <div
                className="flex justify-center items-center"
                style={{
                  width: `${scale * 177.778}px`,
                  height: `${scale * 93.333}px`,
                  marginTop: `${scale * 16}px`,
                }}
              >
                <div
                  className="inline-flex flex-col justify-center items-center"
                  style={{ gap: `${scale * 8.889}px` }}
                >
                  <span
                    className="text-black text-center not-italic font-medium"
                    style={{
                      fontSize: `${scale * 7.407}px`,
                      lineHeight: "normal",
                    }}
                  >
                    {heading ?? "You've made the looooongest streak"}
                  </span>
                  <div
                    className="flex flex-col items-center"
                    style={{ gap: `${scale * 2.963}px` }}
                  >
                    <span
                      className="text-black text-center not-italic font-bold"
                      style={{
                        fontSize: `${scale * 17.778}px`,
                        lineHeight: "normal",
                      }}
                    >
                      {cardValue || 24} days🔥
                    </span>
                    <span
                      className="text-black text-center italic font-normal"
                      style={{
                        fontSize: `${scale * 7.407}px`,
                        lineHeight: "normal",
                      }}
                    >
                      From {startDate ?? "24 Dec'24"} to{" "}
                      {endDate ?? "18 Jan'25"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024TotalLikes = forwardRef<HTMLDivElement, WrappedProps>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      cardValue,
      heading,
    },
    ref
  ) => {
    const scale = width / 200;

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-total-likes"
        children={
          <div
            ref={ref}
            className={cn(
              "relative flex items-center justify-center",
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Total+Likes.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex w-full h-full items-start justify-start z-10">
              <div className="flex flex-col items-center">
                {Array(7)
                  .fill(0)
                  ?.map((_, index) => (
                    <span
                      key={`likes_${index}`}
                      className="text-white text-center not-italic font-bold"
                      style={{
                        fontSize: `${scale * 23.704}px`,
                        lineHeight: "normal",
                      }}
                    >
                      {cardValue?.toLocaleString() || "40,536"}
                    </span>
                  ))}
              </div>
              <div className="w-full h-full flex items-center justify-center">
                <span
                  className="text-white text-center not-italic font-medium"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    width: `${scale * 80.259}px`,
                    lineHeight: "normal",
                  }}
                >
                  {heading ??
                    "This is how much your audience liked your thoughts and images"}
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024TotalComments = forwardRef<
  HTMLDivElement,
  WrappedProps
>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      cardValue,
      heading,
    },
    ref
  ) => {
    const scale = width / 200; // Base size is 200, calculate scale factor

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-total-comments"
        children={
          <div
            ref={ref}
            className={cn(
              `relative flex items-center justify-center`,
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Total+Comments.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-row-reverse w-full h-full items-end justify-end z-10">
              <div className="flex flex-col items-center">
                {Array(7)
                  .fill(0)
                  ?.map((_item, index) => (
                    <span
                      key={`likes_${index}`}
                      className="text-white text-center font-bold"
                      style={{
                        fontSize: `${scale * 23.704}px`,
                        lineHeight: "normal",
                      }}
                    >
                      {cardValue?.toLocaleString() || "40,536"}
                    </span>
                  ))}
              </div>
              <div className="w-full h-full flex items-center justify-center">
                <span
                  className="text-white text-center w-[100px] font-medium"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    width: `${scale * 100}px`,
                  }}
                >
                  {heading ||
                    "This is how much your audience shared their thoughts with you"}
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024MostEngagedPostType = forwardRef<
  HTMLDivElement,
  WrappedProps
>(
  (
    { width = 200, height = 200, containerClassName, className, cardValue },
    ref
  ) => {
    const scale = width / 200;

    const postFormat:
      | "videos"
      | "Visual Posts"
      | "Stories"
      | "repost"
      | "carousel"
      | "feed"
      | "Newsletters" = (() => {
      const postType = cardValue?.toString()?.toLowerCase();
      if (postType?.includes("image")) {
        return "Visual Posts";
      } else if (postType?.includes("video")) {
        return "videos";
      } else if (postType?.includes("newsletter")) {
        return "Newsletters";
      } else if (postType?.includes("feed-shared")) {
        return "repost";
      } else if (
        postType?.includes("document") ||
        postType?.includes("carousel")
      ) {
        return "carousel";
      }
      return "Stories";
    })();

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-engaged-post"
        children={
          <div
            ref={ref}
            className={cn(
              `relative flex items-center justify-center`,
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Most+Engaged+Post.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10">
              <div
                className="relative flex flex-col items-center justify-center"
                style={{
                  width: `${scale * 201}px`,
                  height: `${scale * 135}px`,
                }}
              >
                <div
                  className="inline-flex flex-col items-center"
                  style={{
                    gap: `${scale * 1.481}px`,
                  }}
                >
                  <span
                    className="text-white font-extralight"
                    style={{
                      fontSize: `${scale * 8.148}px`,
                    }}
                  >
                    Your audience especially loves your
                  </span>
                  <span
                    className="text-white uppercase text-center italic font-extrabold"
                    style={{
                      fontSize: `${scale * 14.815}px`,
                    }}
                  >
                    {postFormat || "CAROUSELS"}
                  </span>
                </div>
                <span
                  className="absolute bottom-4 right-5 text-white text-center italic font-extralight"
                  style={{
                    fontSize: `${scale * 6}px`,
                    bottom: `${scale * 16}px`,
                    right: `${scale * 20}px`,
                  }}
                >
                  amongst all the other multimedia posts...
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024MostFavorableDay = forwardRef<
  HTMLDivElement,
  WrappedProps & {
    impressions: string | number;
  }
>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      cardValue,
      impressions,
    },
    ref
  ) => {
    const scale = width / 200;

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-favorable-day"
        children={
          <div
            ref={ref}
            className={cn(
              `relative flex items-center justify-center`,
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Favourable+Day.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex flex-col w-full h-full items-center justify-center z-10">
              <div
                className="relative flex flex-col items-center justify-center"
                style={{
                  width: `${scale * 201}px`,
                  height: `${scale * 135}px`,
                }}
              >
                <div
                  className="flex flex-col items-center"
                  style={{
                    gap: `${2.963}px`,
                  }}
                >
                  <span
                    className="text-black text-center font-normal"
                    style={{
                      fontSize: `${scale * 7.407}px`,
                      lineHeight: "normal",
                    }}
                  >
                    You hit the jackpot on
                  </span>
                  <span
                    className="text-black text-center font-bold"
                    style={{
                      fontSize: `${scale * 14.815}px`,
                      lineHeight: "normal",
                    }}
                  >
                    {cardValue || "15 September'24"}
                  </span>
                </div>
                <span
                  className="text-black text-center italic font-light"
                  style={{
                    fontSize: `${scale * 6.667}px`,
                    marginTop: `${scale * 12}px`,
                    lineHeight: "normal",
                  }}
                >
                  with{" "}
                  <span className="text-[#084AC6]">
                    {impressions?.toLocaleString() || "36,528"}
                  </span>{" "}
                  impressions
                </span>
                {/* <span
            className="absolute text-center italic font-extralight bg-clip-text text-transparent"
            style={{
              fontSize: `${scale * 6}px`,
              bottom: `${scale * 20}px`,
              right: `${scale * 32}px`,
              lineHeight: "normal",
              background: "linear-gradient(90deg, #FF6B6B 0%, #4ECDC4 100%)",
              WebkitBackgroundClip: "text",
            }}
          >
            #highestimpressions
          </span> */}
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

export const Wrapped2024MonthlyTrend = forwardRef<
  HTMLDivElement,
  {
    monthlyTrend: MonthlyTrendData[];
  } & WrappedProps
>(
  (
    {
      width = 200,
      height = 200,
      containerClassName,
      className,
      monthlyTrend,
      heading,
      subheading,
      footerText,
    },
    ref
  ) => {
    const scale = width / 200; // Base size is 200, calculate scale factor

    const MONTHS = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];

    const getMonthIcon = (
      month: number,
      monthlyTrend: MonthlyTrendData[],
      scale: number
    ) => {
      const monthData = monthlyTrend?.find((data) => data._id === month + 1);

      if (!monthData) {
        return <IoMdTrendingDown size={scale * 8} color="#FF4444" />;
      }

      const sortedMonths = [...monthlyTrend]?.sort(
        (a, b) => b.totalEngagement - a.totalEngagement
      );

      const position = sortedMonths?.findIndex(
        (data) => data._id === monthData._id
      );

      if (position < 3) {
        // Top 3 months - show heart
        return <FaHeart size={scale * 8} color="#ff0000" />;
      } else if (position < 8) {
        // Next 5 months - show trending up
        return <IoMdTrendingUp size={scale * 8} color="#22C55E" />;
      } else {
        // Remaining months - show trending down
        return <IoMdTrendingDown size={scale * 8} color="#f2873e" />;
      }
    };

    return (
      <YearWrap2024CardsContainer
        className={containerClassName}
        ref={ref}
        scale={scale}
        id="wrapped-monthly-trend"
        children={
          <div
            ref={ref}
            className={cn(
              `relative flex items-center justify-center`,
              className
            )}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundImage: `url(${host}/api/file/proxyImage?imageUrl=https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/Monthly+Trend.png)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* Foreground Content */}
            <div
              className="flex flex-col w-full h-full items-center justify-start z-10"
              style={{
                fontSize: `${scale * 8}px`, // Scale base font size
              }}
            >
              {/* Title */}
              <div className="flex flex-col items-center justify-center">
                <span
                  className="text-white text-center font-semibold"
                  style={{
                    marginTop: `${scale * 21.48}px`,
                    fontSize: `${scale * 8.889}px`, // Scale text size
                    lineHeight: `${scale * 4}px`,
                  }}
                >
                  {heading ?? "2024 was magical..."}
                </span>
                <span
                  className="text-[#3DA8FF] italic font-normal"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                    lineHeight: `${scale * 4}px`,
                    marginTop: `${scale * 8}px`,
                  }}
                >
                  {subheading ?? "with all highs and lows"}
                </span>
              </div>

              {/* Monthly Trends */}
              <div
                className="flex flex-col gap-[14px] items-center justify-center"
                style={{
                  marginTop: `${scale * 16.48}px`,
                  gap: `${scale * 14}px`,
                }}
              >
                <div
                  className="flex flex-wrap items-center justify-center"
                  style={{
                    gap: `${scale * 14}px`,
                  }}
                >
                  {MONTHS.map((month, index) => (
                    <div
                      key={`monthlyTrend${index}`}
                      className="flex flex-col justify-center items-center gap-1"
                      style={{
                        width: `${scale * 32}px`,
                        gap: `${scale * 1}px`,
                      }}
                    >
                      {getMonthIcon(index, monthlyTrend, scale)}
                      <span
                        className="text-white text-center font-medium"
                        style={{
                          fontSize: `${scale * 7.407}px`,
                        }}
                      >
                        {month}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Text */}
              <div
                className="items-end w-full h-full justify-end flex pb-5 pr-3 "
                style={{
                  width: "95%",
                }}
              >
                <span
                  className="text-white italic font-light"
                  style={{
                    fontSize: `${scale * 7.407}px`,
                  }}
                >
                  {footerText ?? "a roller-coaster ride.."}
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  }
);

interface wrappedContainerProps {
  children: React.ReactNode;
  scale: number;
  name?: string;
  image?: string;
  id?: string;
  className?: string;
}

export const YearWrap2024CardsContainer = forwardRef<
  HTMLDivElement,
  wrappedContainerProps
>(({ children, scale, id, className }, ref) => {
  const authState = useAuth();

  return (
    <>
      {authState?.loggedUserLKdProfile && (
        <div
          ref={ref}
          id={id}
          className={cn("flex flex-col  justify-center", className)}
        >
          {children}
          <div
            style={{
              padding: `${scale * 6}px ${scale * 8}px`,
              gap: `${scale * 10}px`,
            }}
            className="w-full flex items-stretch justify-between self-stretch bg-[linear-gradient(359deg,#000_6.12%,#000_61.58%)] rounded-[0px_0px_2.96px_2.96px] "
          >
            <div
              style={{
                gap: `${scale * 6}px`,
              }}
              className="flex flex-1"
            >
              <div
                style={{
                  width: `${scale * 20}px`,
                  height: `${scale * 20}px`,
                }}
                className="rounded-full"
              >
                <img
                  src={`${host}/api/file/proxyImage?imageUrl=${encodeURIComponent(
                    authState?.loggedUserLKdProfile?.profile_image
                  )}`}
                  style={{
                    width: `${scale * 20}px`,
                    height: `${scale * 20}px`,
                  }}
                  className="rounded-full "
                  alt=""
                />
              </div>

              <div
                style={{
                  padding: `${scale * 2}px 0`,
                  gap: `${scale * 2}px`,
                }}
                className="flex flex-col justify-center   "
              >
                <span
                  style={{
                    fontSize: `${scale * 5}px`,
                    // lineHeight: `${scale * 11.852}px`,
                    lineHeight: "normal",
                  }}
                  className="text-white not-italic font-bold"
                >
                  {authState?.loggedUserLKdProfile?.name}
                </span>
                <span
                  style={{
                    fontSize: `${scale * 4}px`,
                    // lineHeight: `${scale * 11.852}px`,
                    lineHeight: "normal",
                  }}
                  className="text-[#DC2627] not-italic font-semibold"
                >
                  #MyLinkedInUnwrapped
                </span>
              </div>
            </div>
            <div
              style={{
                gap: `${scale * 2}px`,
              }}
              className="flex justify-center flex-1 items-center "
            >
              <div className="h-full flex items-center justify-center">
                <FaAward size={`${scale * 12}`} color="#806260E5" />
              </div>

              <div
                style={{
                  fontSize: `${scale * 6}px`,
                  lineHeight: "normal",
                }}
                className="not-italic h-full items-center justify-center text-center flex text-[#806260]"
              >
                ForLinkedInCreatorsOnly
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
